if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        this.teardownStickyAtc();
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && cartDrawer.classList.contains('active')) {
          document.body.classList.add('overflow-hidden');
        }
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
            this.setupStickyAtc();
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      // Mobile only: a floating Add to Cart that appears while the real button
      // inside the popup is scrolled out of view, and hides once it's in view.
      setupStickyAtc() {
        this.teardownStickyAtc();
        if (!window.matchMedia('(max-width: 749px)').matches) return;
        const realButtons = this.querySelector('.product-form__buttons');
        const realBtn = realButtons && realButtons.querySelector('button[name="add"], .product-form__submit');
        if (!realBtn) return;

        const bar = document.createElement('div');
        bar.className = 'qa-sticky-atc';
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'button button--full-width button--primary qa-sticky-atc__btn';
        btn.innerHTML = '<span>' + (realBtn.textContent.trim() || 'Add to cart') + '</span>';
        btn.disabled = realBtn.disabled;
        // Mirror the real button's exact look so the floating bar matches the
        // product page (it sits on <body>, which would otherwise give it the
        // default scheme instead of the product's button color).
        const rs = getComputedStyle(realBtn);
        btn.style.backgroundColor = rs.backgroundColor;
        btn.style.color = rs.color;
        btn.style.borderColor = rs.borderColor;
        btn.style.borderRadius = rs.borderRadius;
        btn.addEventListener('click', () => realBtn.click());
        bar.appendChild(btn);
        document.body.appendChild(bar);
        this._qaStickyBar = bar;

        this._qaAtcObserver = new IntersectionObserver(
          (entries) => entries.forEach((e) => bar.classList.toggle('qa-sticky-atc--show', !e.isIntersecting)),
          { threshold: 0 }
        );
        this._qaAtcObserver.observe(realBtn);
      }

      teardownStickyAtc() {
        if (this._qaAtcObserver) {
          this._qaAtcObserver.disconnect();
          this._qaAtcObserver = null;
        }
        if (this._qaStickyBar) {
          this._qaStickyBar.remove();
          this._qaStickyBar = null;
        }
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
        this.enhanceQuickAddMarkup(productElement);
        this.removeSelfPerpetuatingScripts(productElement);
      }

      // Quick-add injects the product page markup, and HTMLUpdateUtility re-executes any
      // <script> inside it. A PDP script that starts a setInterval therefore starts a NEW
      // timer on every popup open, and once the popup closes its target element is gone, so
      // the callback throws about once a second, forever, and the timers accumulate.
      // Strip only those: src="" scripts still define the custom elements the popup needs
      // (product-info, product-form, media-gallery) on pages that do not already load them,
      // and application/json + ld+json blocks carry the variant data product-info reads.
      removeSelfPerpetuatingScripts(productElement) {
        productElement.querySelectorAll('script').forEach((s) => {
          if (s.src) return;
          const type = (s.getAttribute('type') || '').toLowerCase();
          if (type.indexOf('json') !== -1) return;
          if (/setInterval\s*\(/.test(s.textContent || '')) s.remove();
        });
      }

      enhanceQuickAddMarkup(productElement) {
        // 1. Wrap price and details link for mobile side-by-side flex layout
        const priceEl = productElement.querySelector('.price');
        const detailsEl = productElement.querySelector('.product__view-details');
        if (priceEl && detailsEl) {
          if (priceEl.parentNode.classList.contains('quick-add-price-details-wrapper')) {
            return;
          }
          const priceDetailsWrapper = document.createElement('div');
          priceDetailsWrapper.className = 'quick-add-price-details-wrapper';
          priceEl.parentNode.insertBefore(priceDetailsWrapper, priceEl);
          priceDetailsWrapper.appendChild(priceEl);
          priceDetailsWrapper.appendChild(detailsEl);
        }
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());

        // Remove .tiered-quantity-selector if present
        const tieredQuantitySelector = productElement.querySelector('.tiered-quantity-selector');
        if (tieredQuantitySelector) tieredQuantitySelector.remove();
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
